import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { auth } from "@startupp-cloud/startupp-ai-sdk";
import { useLocation, useNavigate } from "react-router-dom";
import {
  tokenStorage,
  userStorage,
  sessionManager,
  clearAllAuthData,
} from "../utils/secureStorage";
import { getSidebarParam } from "../components/Utils";

// Import setConfig safely
let setConfig;
try {
  const sdkModule = require("@startupp-cloud/startupp-ai-sdk");
  setConfig = sdkModule.setConfig;
} catch (error) {
  console.warn("setConfig not available from SDK, using fallback");
}

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} name
 * @property {string} email
 * @property {string} [avatar]
 * @property {string} [organizationId]
 * @property {string} [role]
 */

/**
 * @typedef {Object} AuthError
 * @property {string} code
 * @property {string} message
 * @property {any} [details]
 */

/**
 * @typedef {'initializing' | 'authenticated' | 'unauthenticated' | 'authenticating' | 'error'} AuthState
 */

/**
 * @typedef {Object} AuthCapabilities
 * @property {boolean} isInIframe
 * @property {boolean} isMobile
 * @property {boolean} supportsPopups
 * @property {boolean} canUseRedirect
 * @property {'popup' | 'redirect'} recommendedMethod
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {AuthState} authState
 * @property {User|null} user
 * @property {AuthError|null} error
 * @property {boolean} loading
 * @property {boolean} ready
 * @property {AuthCapabilities|null} capabilities
 * @property {() => Promise<void>} login
 * @property {(method: 'popup' | 'redirect' | 'auto') => Promise<void>} loginWithMethod
 * @property {() => Promise<void>} logout
 * @property {() => void} clearError
 * @property {() => Promise<void>} refreshUser
 * @property {boolean} isAuthenticated
 * @property {(role: string) => boolean} hasRole
 * @property {() => string|null} getToken
 */

// Create context
const AuthContext = createContext(null);

/**
 * @typedef {Object} AuthProviderProps
 * @property {React.ReactNode} children
 * @property {Object} [config]
 * @property {string} [config.apiBaseUrl]
 * @property {string} [config.authBaseUrl]
 * @property {string} [config.organizationId]
 * @property {string} [config.apiKey]
 * @property {(state: AuthState, user: User|null, error: AuthError|null) => void} [onAuthStateChange]
 * @property {boolean} [enableDebugMode]
 */

// Provider component
export const AuthProvider = ({
  children,
  config,
  onAuthStateChange,
  enableDebugMode = false,
}) => {
  // State
  const [authState, setAuthState] = useState("initializing");
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState(null);
  const [isAllowedIP, setIsAllowedIP] = useState(false);
  const [isSidebarSet, setIsSidebarSet] = useState(false);

  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const initializedRef = useRef(false);

  // Debug logging - ALWAYS enabled for debugging
  const debugLog = useCallback((message, data) => {
    console.log(`[AuthProvider] ${message}`, data);
  }, []);

  // Check if current IP is allowed
  const checkAllowedIP = useCallback(async () => {
    try {
      debugLog("🌐 Starting IP check...");
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      const currentIP = data.ip;

      // Get allowed IPs from environment variable
      const allowedIPs = process.env.REACT_APP_ALLOWED_IPS?.split(",") || [];
      const isAllowed = allowedIPs.includes(currentIP);

      debugLog("🌐 IP check result:", { currentIP, allowedIPs, isAllowed });
      setIsAllowedIP(isAllowed);
      return isAllowed;
    } catch (err) {
      debugLog("❌ IP check failed:", err);
      return false;
    }
  }, [debugLog]);

  // Check if sidebar is set
  const checkSidebarSet = useCallback(() => {
    const sidebar = localStorage.getItem("sidebar");
    const isSet = !!sidebar;
    debugLog("📱 Sidebar check result:", { sidebar, isSet });
    setIsSidebarSet(isSet);
    return isSet;
  }, [debugLog]);

  // Auto login with credentials
  const autoLogin = useCallback(async () => {
    try {
      debugLog("🔐 Attempting auto login...");
      const username = process.env.REACT_APP_API_USER;
      const password = process.env.REACT_APP_API_PASSWORD;

      if (!username || !password) {
        debugLog("❌ Auto login credentials not configured");
        throw new Error("Auto login credentials not configured");
      }

      debugLog("🔐 Auto login credentials found, attempting login...");
      // Perform login with credentials
      await auth.loginWithCredentials(username, password);

      // Get user info
      const userInfo = await auth.getUserInfo();
      debugLog("✅ Auto login successful:", userInfo);

      setUser(userInfo);
      return true;
    } catch (err) {
      debugLog("❌ Auto login failed:", err);
      return false;
    }
  }, [debugLog]);

  // Error handling
  const handleError = useCallback(
    (err, context) => {
      debugLog(`❌ Error in ${context}:`, err);

      let authError;

      if (err.response?.data?.error?.code) {
        authError = {
          code: err.response.data.error.code,
          message:
            err.response.data.error.message || "Authentication error occurred",
          details: err.response.data,
        };
      } else if (err.response?.status === 401) {
        authError = {
          code: "UNAUTHORIZED",
          message: "Your session has expired. Please sign in again.",
          details: err.response,
        };
      } else if (err.response?.status === 429) {
        authError = {
          code: "RATE_LIMITED",
          message: "Too many requests. Please wait before trying again.",
          details: err.response,
        };
      } else if (err.code === "NETWORK_ERROR") {
        authError = {
          code: "NETWORK_ERROR",
          message: "Network error. Please check your connection.",
          details: err,
        };
      } else {
        authError = {
          code: "UNKNOWN_ERROR",
          message: err.message || "An unexpected error occurred",
          details: err,
        };
      }

      setError(authError);
      return authError;
    },
    [debugLog]
  );

  // Update auth state
  const updateAuthState = useCallback(
    (newState, newUser = null, newError = null) => {
      debugLog(`🔄 Auth state changing: ${authState} -> ${newState}`, {
        user: newUser,
        error: newError,
      });

      // Debug: Log auth state changes if needed
      console.log("[AuthContext] updateAuthState called:", {
        oldState: authState,
        newState,
        newUser: !!newUser,
        newError: !!newError,
      });

      setAuthState(newState);
      setUser(newUser);
      setError(newError);
      setLoading(newState === "initializing" || newState === "authenticating");

      // Notify external listeners
      onAuthStateChange?.(newState, newUser, newError);
    },
    [authState, debugLog, onAuthStateChange]
  );

  // Initialize SDK configuration
  const initializeSDK = useCallback(() => {
    try {
      debugLog(
        "⚙️ SDK already initialized in index.js, skipping duplicate initialization"
      );

      // Just log the current SDK config for debugging
      try {
        const { getConfig } = require("@startupp-cloud/startupp-ai-sdk");
        const currentConfig = getConfig();
        debugLog("⚙️ Current SDK config:", {
          ...currentConfig,
          apiKey: currentConfig?.apiKey
            ? `${currentConfig.apiKey.substring(
                0,
                8
              )}...${currentConfig.apiKey.substring(
                currentConfig.apiKey.length - 4
              )} (length: ${currentConfig.apiKey.length})`
            : "NOT SET",
        });
      } catch (error) {
        debugLog("⚠️ Could not get current SDK config:", error);
      }
    } catch (err) {
      debugLog("❌ SDK check failed:", err);
      handleError(err, "SDK check");
    }
  }, [config, handleError, debugLog]);

  // Check authentication status
  const checkAuthStatus = useCallback(async () => {
    try {
      debugLog("🔍 Checking authentication status...");

      // First, try to restore tokens from secure storage
      const storedTokens = tokenStorage.getTokens();
      const storedUser = userStorage.getUser();

      debugLog("💾 Stored data check:", {
        hasStoredTokens: !!storedTokens,
        hasStoredUser: !!storedUser,
      });

      if (storedTokens && storedTokens.accessToken) {
        debugLog("💾 Found stored tokens, restoring to SDK singleton...");
        
        // Set tokens in SDK singleton
        auth.setTokens(storedTokens);
        
        // Verify SDK has the tokens
        const sdkTokens = auth.getTokens();
        debugLog("🔑 SDK tokens after restoration:", {
          hasAccessToken: !!sdkTokens.accessToken,
          hasRefreshToken: !!sdkTokens.refreshToken,
        });

        if (storedUser) {
          debugLog("💾 Found stored user, using cached data");
          setUser(storedUser);
          return storedUser; // Return the stored user
        }
      }

      // Check if we have tokens in SDK singleton
      const tokens = auth.getTokens();
      debugLog("🔑 SDK singleton tokens check:", {
        hasAccessToken: !!tokens.accessToken,
        hasRefreshToken: !!tokens.refreshToken,
      });

      if (!tokens.accessToken) {
        debugLog("❌ No access token found in SDK singleton");
        return null;
      }

      // Verify tokens by getting user info from SDK
      debugLog("👤 Fetching user info from API using SDK singleton...");
      const userInfo = await auth.getUserInfo();
      debugLog("✅ User info retrieved from SDK:", userInfo);

      if (!userInfo) {
        debugLog("❌ No user info returned from SDK");
        return null;
      }

      setUser(userInfo);

      // Update stored user info
      userStorage.setUser(userInfo);

      return userInfo; // Return the user info
    } catch (err) {
      debugLog("❌ Auth status check failed:", err);

      // Clear any invalid tokens from both SDK singleton and storage
      auth.clearTokens();
      tokenStorage.clearTokens();

      // Don't set error for normal unauthenticated state
      if (
        err.response?.status === 401 ||
        err.response?.data?.error?.code === "USER_AUTH_REQUIRED"
      ) {
        debugLog("ℹ️ Normal unauthenticated state");
        return null;
      }

      // Handle other errors
      handleError(err, "authentication status check");
      return null;
    }
  }, [debugLog, handleError]);

  // Initialize authentication
  const initializeAuth = useCallback(async () => {
    try {
      debugLog("🚀 Initializing authentication...");
      setLoading(true);

      // Initialize SDK
      initializeSDK();

      // Check IP and sidebar status
      debugLog("🔍 Checking IP and sidebar status...");
      const isAllowed = await checkAllowedIP();
      const hasSidebar = checkSidebarSet();

      debugLog("📊 Environment check results:", { isAllowed, hasSidebar });

      // TEMPORARILY DISABLED: Auto-login to force custom login component
      // if (isAllowed && hasSidebar) {
      //   const autoLoginSuccess = await autoLogin();
      //   if (autoLoginSuccess) {
      //     updateAuthState("authenticated", user);
      //     return;
      //   }
      // }

      // Check authentication status
      debugLog("🔍 Checking current authentication status...");
      const userInfo = await checkAuthStatus();

      // Update state
      debugLog("🔄 Updating auth state based on check result:", {
        hasUser: !!userInfo,
      });
      updateAuthState(userInfo ? "authenticated" : "unauthenticated", userInfo);

      // Route-based redirects are now handled in a separate useEffect
    } catch (err) {
      debugLog("❌ Authentication initialization failed:", err);
      handleError(err, "authentication initialization");
      updateAuthState("error", null, err);
    } finally {
      debugLog(
        "🏁 Authentication initialization complete, setting loading to false"
      );
      setLoading(false);
    }
  }, [
    debugLog,
    initializeSDK,
    checkAllowedIP,
    checkSidebarSet,
    checkAuthStatus,
    updateAuthState,
    handleError,
  ]);

  // Initialize on mount and only re-run if location changes
  useEffect(() => {
    debugLog("🎯 useEffect triggered for initialization", {
      initialized: initializedRef.current,
      authState,
      pathname: location.pathname,
    });

    // Prevent re-initialization if already initialized
    if (initializedRef.current) {
      debugLog("⏭️ Already initialized, skipping");
      return;
    }

    // Prevent re-initialization if already authenticated
    if (authState === "authenticated") {
      debugLog("⏭️ Already authenticated, skipping initialization");
      return;
    }

    debugLog("▶️ Starting initialization...");
    initializedRef.current = true;
    initializeAuth();
  }, []); // Empty dependency array - only run once on mount

  // Handle route-based redirects without re-initializing auth
  useEffect(() => {
    debugLog("🧭 Route redirect check", {
      loading,
      authState,
      pathname: location.pathname,
    });

    // Only redirect if auth is ready and user is not authenticated
    if (
      !loading &&
      authState === "unauthenticated" &&
      location.pathname !== "/login" &&
      location.pathname !== "/auth/callback"
    ) {
      // Check if sidebar parameter is present to determine if we should delay
      const hasSidebarParam = getSidebarParam();

      const storedTVUrl = localStorage.getItem("tvUrl");
      if (!storedTVUrl) {
        if (hasSidebarParam) {
          // Delay redirect when sidebar param is found to allow auto-login to complete
          debugLog("🔀 Delaying redirect to login page (sidebar param found)");
          setTimeout(() => {
            navigate("/login");
          }, 4000);
        } else {
          debugLog("🔀 Redirecting to login page");
          navigate("/login");
        }
      } else if (location.pathname !== storedTVUrl) {
        window.location.href = storedTVUrl;
      }
    }
  }, [location.pathname, authState, loading, navigate, debugLog]);

  // Login function
  const login = useCallback(async () => {
    try {
      debugLog("🔐 Starting login process...");
      updateAuthState("authenticating");

      // Store the current URL as return URL for after authentication
      if (
        location.pathname !== "/login" &&
        location.pathname !== "/auth/callback"
      ) {
        window.sessionStorage.setItem(
          "auth_return_url",
          location.pathname + location.search
        );
        debugLog("💾 Stored return URL:", location.pathname + location.search);
      }

      // Use smart login from SDK
      debugLog("🚀 Calling auth.loginSmart()...");
      await auth.loginSmart();

      // Note: After redirect, the user will land on /auth/callback
      // The AuthCallback component will handle token storage and redirect
      debugLog("✅ Login initiated successfully");
    } catch (err) {
      debugLog("❌ Login failed:", err);
      handleError(err, "login");
      updateAuthState("error", null, error);
    }
  }, [
    debugLog,
    updateAuthState,
    error,
    handleError,
    location.pathname,
    location.search,
  ]);

  // Login with specific method
  const loginWithMethod = useCallback(
    async (method) => {
      try {
        debugLog(`🔐 Starting login process with method: ${method}`);
        updateAuthState("authenticating");

        // Store the current URL as return URL for after authentication
        if (
          location.pathname !== "/login" &&
          location.pathname !== "/auth/callback"
        ) {
          window.sessionStorage.setItem(
            "auth_return_url",
            location.pathname + location.search
          );
          debugLog(
            "💾 Stored return URL:",
            location.pathname + location.search
          );
        }

        // Use SDK login methods
        if (method === "popup") {
          debugLog("🪟 Using popup login method");
          await auth.loginWithPopup();

          // For popup method, check auth status immediately
          debugLog("🔍 Checking auth status after popup login");
          const userInfo = await checkAuthStatus();
          if (userInfo) {
            debugLog("✅ Popup login successful, updating state");
            updateAuthState("authenticated", userInfo);

            // Navigate to return URL or home
            const returnUrl = window.sessionStorage.getItem("auth_return_url");
            window.sessionStorage.removeItem("auth_return_url");
            debugLog("🔀 Navigating after popup login:", returnUrl || "/");
            navigate(returnUrl || "/");
          } else {
            throw new Error("Login failed");
          }
        } else if (method === "redirect") {
          debugLog("🔀 Using redirect login method");
          await auth.loginSmart();
          // Note: After redirect, the user will land on /auth/callback
        } else {
          debugLog(`🔧 Using custom login method: ${method}`);
          await auth.loginWithMethod(method);
        }
      } catch (err) {
        debugLog(`❌ Login with method ${method} failed:`, err);
        const authError = handleError(err, "login with method");
        updateAuthState("error", null, authError);
      }
    },
    [
      debugLog,
      updateAuthState,
      checkAuthStatus,
      user,
      error,
      handleError,
      location.pathname,
      location.search,
      navigate,
    ]
  );

  // Logout function
  const logout = useCallback(async () => {
    try {
      debugLog("🚪 Starting logout process...");
      setLoading(true);

      // Use SDK advanced logout for complete cleanup
      await auth.advancedLogout();

      // Clear all stored authentication data
      clearAllAuthData();

      // Update state
      updateAuthState("unauthenticated", null);

      // Navigate to login
      navigate("/login");
      debugLog("✅ Logout completed successfully");
    } catch (err) {
      debugLog("❌ Logout failed:", err);
      handleError(err, "logout");

      // Even if logout fails, clear local data
      clearAllAuthData();
      updateAuthState("unauthenticated", null);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [debugLog, updateAuthState, handleError, navigate]);

  // Clear error
  const clearError = useCallback(() => {
    debugLog("🧹 Clearing error");
    setError(null);
  }, [debugLog]);

  // Refresh user info
  const refreshUser = useCallback(async () => {
    try {
      debugLog("🔄 Refreshing user info from SDK singleton...");
      setLoading(true);

      // Force SDK to use current tokens and get fresh user info
      const userInfo = await auth.getUserInfo();
      
      if (!userInfo) {
        debugLog("❌ No user info returned from SDK singleton");
        throw new Error("Failed to retrieve user information");
      }

      debugLog("✅ User info refreshed from SDK singleton:", userInfo);
      
      // Update both local state and storage
      setUser(userInfo);
      userStorage.setUser(userInfo);
      
      // Update auth state to authenticated
      updateAuthState("authenticated", userInfo);
      
    } catch (err) {
      debugLog("❌ User refresh failed:", err);
      handleError(err, "user refresh");
      
      // If refresh fails, clear tokens and set to unauthenticated
      auth.clearTokens();
      tokenStorage.clearTokens();
      userStorage.clearUser();
      updateAuthState("unauthenticated", null);
    } finally {
      setLoading(false);
    }
  }, [debugLog, handleError, updateAuthState]);

  // Check if user has specific role
  const hasRole = useCallback(
    (role) => {
      const result = user?.role === role;
      debugLog(`👤 Role check for '${role}':`, result);
      return result;
    },
    [user, debugLog]
  );

  // Get current token
  const getToken = useCallback(() => {
    const tokens = auth.getTokens();
    const hasToken = !!tokens.accessToken;
    debugLog("🔑 Token check:", { hasToken });
    return tokens.accessToken;
  }, [debugLog]);

  // Create context value
  const contextValue = useMemo(() => {
    const value = {
      // State
      authState,
      user,
      error,
      loading,
      ready: !loading,
      capabilities,
      isAllowedIP,
      isSidebarSet,

      // Actions
      login,
      loginWithMethod,
      logout,
      clearError,
      refreshUser,

      // Utilities
      isAuthenticated: authState === "authenticated",
      hasRole,
      getToken,
    };

    debugLog("📦 Context value updated:", {
      authState: value.authState,
      hasUser: !!value.user,
      loading: value.loading,
      ready: value.ready,
      isAuthenticated: value.isAuthenticated,
    });

    return value;
  }, [
    authState,
    user,
    error,
    loading,
    capabilities,
    isAllowedIP,
    isSidebarSet,
    login,
    loginWithMethod,
    logout,
    clearError,
    refreshUser,
    hasRole,
    getToken,
    debugLog,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

// Convenience hook for common auth operations
export const useAuth = () => {
  const auth = useAuthContext();

  return {
    ...auth,
    // Additional convenience methods
    requireAuth: useCallback(async () => {
      if (!auth.isAuthenticated) {
        await auth.login();
      }
    }, [auth]),

    loginIfNeeded: useCallback(async () => {
      if (auth.authState === "unauthenticated") {
        await auth.login();
      }
    }, [auth]),

    isAdmin: auth.hasRole("admin"),
    isUser: auth.hasRole("user"),
  };
};
